<template>
  <div>
    <div class="d-flex align-center">
      <h2>請求一覧</h2>
      <v-spacer />
      <div style="width: 180px">
        <v-select
          variant="outlined"
          hide-details=""
          density="compact"
          :items="invoiceStatusOptions"
          item-value="value"
          item-title="label"
          v-model="invoiceStatus"
        >
        </v-select>
      </div>
      <v-btn
        @click="updateStatus"
        :disabled="!invoiceStatus || selected.length == 0"
        color="primary"
        depressed
        variant="flat"
        class="mx-1"
      >
        保存
      </v-btn>
    </div>

    <div class="my-4">
      <month-pager v-model="yearMonth" />
    </div>

    <div>
      <div v-if="importable && !isFetching" class="pa-1">
        <v-btn
          depressed
          color="primary"
          @click="() => (fileUploadModal = true)"
          class="mr-4"
          :disabled="isProcessingImport"
        >
          <span v-if="isProcessingImport">
            <v-progress-circular indeterminate size="20" width="3" />
          </span>
          <span v-else>売掛金残高をインポート</span>
        </v-btn>
      </div>

      <div v-if="exportable && !isFetching" class="pa-1">
        <v-btn
          variant="outlined"
          color="primary"
          @click="exportSales"
          class="mr-3"
          :disabled="isProcessingExport"
        >
          <span v-if="isProcessingExport">
            <v-progress-circular indeterminate size="20" width="3" />
          </span>
          <span v-else>売上情報をエクスポート</span>
        </v-btn>
      </div>
    </div>

    <v-data-table
      v-model="selected"
      :headers="tableHeaders"
      :items="invoices"
      :loading="isFetching"
      hide-default-footer
      show-select
      item-value="nurseryId"
      :items-per-page="-1"
      return-object
      class="custom-dense-table"
    >
      <template v-slot:[`item.total`]="{ item }">
        <strong>
          {{ item.total }}
        </strong>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        {{ getInvoiceStatusText(item.status) }}
      </template>
      <template v-slot:[`item.detail`]="{ item }">
        <v-btn
          class="mx-1"
          depressed
          color="primary"
          :to="`/invoice/${item.nurseryId}/${yearMonth}`"
          :disabled="!item.status"
        >
          詳細
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog max-width="720" v-model="fileUploadModal">
      <v-card class="pa-4">
        <h2 class="mb-4">売掛金残高をインポート</h2>
        <v-file-input
          v-model="fileToUpload"
          label="ファイルを選択（CSV）"
          outlined
          accept="text/csv"
          class="mb-4"
        />
        <v-progress-linear v-if="isProcessingImport" class="my-2" color="primary" indeterminate />
        <div class="d-flex">
          <v-spacer />
          <v-btn
            color="primary"
            depressed
            large
            :disabled="!fileToUpload || isProcessingImport"
            @click="upload"
          >
            アップロード
          </v-btn>
          <v-spacer />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import MonthPager from "@/components/atoms/MonthPager.vue";

import { invoiceModule } from "@/store/viewModule/invoicesModule";
import dayjs from "dayjs";
import LocalDataService from "@/service/LocalDataService";
import ApiResource from "@/api/ApiResource";
import CommonUtil from "@/utils/CommonUtils";
import { OperationInvoiceResponse } from "chaild-api/lib";
import { invoiceStatusItems } from "@/constants/invoice";
import { schoolInvoiceModule } from "@/store/viewModule/schoolInvoiceModule";

interface DataType {
  yearMonth: string;
  isFetching: boolean;

  // import
  isProcessingImport: boolean;
  fileUploadModal: boolean;
  fileToUpload: File | null;
  importPollingTimerId: number | null;

  // export
  isProcessingExport: boolean;
  exportPollingTimerId: number | null;

  tableHeaders: any[];
  selected: OperationInvoiceResponse[];
  invoiceStatus: string | null;
}

export default defineComponent({
  components: {
    MonthPager,
  },
  computed: {
    invoiceStatusOptions() {
      return invoiceStatusItems;
    },
    invoices() {
      // ソートを正常に機能させるため、事前に各アイテムの total (請求額) を算出して返却
      return invoiceModule.invoices.map((invoice: any) => ({
        ...invoice,
        total: (invoice.nurseryFee || 0) + (invoice.nurseryAdditionalFee || 0) + (invoice.nurseryServiceFee || 0)
      }));
    },
    importable() {
      return invoiceModule.impotarble;
    },
    exportable() {
      return invoiceModule.exportable;
    },
    year(): number {
      return parseInt(dayjs(this.yearMonth).format("YYYY"));
    },
    month(): number {
      return parseInt(dayjs(this.yearMonth).format("MM"));
    },
    importStatus() {
      return invoiceModule.importStatus;
    },
    exportStatus() {
      return invoiceModule.exportStatus;
    },
  },
  data: (): DataType => ({
    yearMonth: dayjs().subtract(1, "month").format("YYYY-MM"),
    isFetching: false,
    // import
    isProcessingImport: false,
    fileUploadModal: false,
    fileToUpload: null,
    importPollingTimerId: null,
    // export
    isProcessingExport: false,
    exportPollingTimerId: null,

    // Vuetify 3 に合わせて value から key に変更
    tableHeaders: [
      { title: "施設名", key: "nurseryName", width: 180 },
      { title: "部門コード", key: "departmentCode", width: 74 },
      { title: "保育料", key: "nurseryFee", width: 61 },
      { title: "延長保育料", key: "nurseryAdditionalFee", width: 74 },
      { title: "園提供サービス料", key: "nurseryServiceFee", width: 110 },
      { title: "請求額", key: "total", width: 52, align: "end" },
      { title: "ステータス", key: "status", width: 90 },
      { title: "", key: "detail", sortable: false, width: 80 }, // ボタン列はソート対象外
    ],
    selected: [],
    invoiceStatus: null,
  }),
  watch: {
    yearMonth(newVal) {
      // 1. URLを更新（パラメータを新しい年月にする）
      if (this.$route.params.yearMonth !== newVal) {
        this.$router.replace({ path: `/invoice/${newVal}` });
      }
      // 2. データを再取得
      this.listInvoices();
    },
  },
  methods: {
    async listInvoices() {
      const month = dayjs(this.yearMonth).month() + 1;
      this.isFetching = true;
      await invoiceModule.listInvoices({
        year: CommonUtil.getFiscalYear(this.yearMonth),
        month,
      });
      this.isFetching = false;
    },
    async updateStatus() {
      if (this.selected.length > 0 && this.invoiceStatus != null) {
        this.isFetching = true;

        const nurseryIds = this.selected.map((s: any) => {
          return typeof s === 'object' && s !== null ? s.nurseryId : s;
        });

        const response = await schoolInvoiceModule
          .updateStatus({
            nurseryIds: nurseryIds,
            month: dayjs(this.yearMonth).month() + 1,
            year: CommonUtil.getFiscalYear(this.yearMonth),
            status: this.invoiceStatus,
          })
          .catch(() => (this.isFetching = false));
        if (response) {
          alert("確定しました");
          await this.listInvoices();
        }
        this.isFetching = false;
      }
    },
    // import
    async upload() {
      const company = LocalDataService.getCompany();
      if (company && this.fileToUpload) {
        this.isProcessingImport = true;
        try {
          const resourceInfo = await ApiResource.getUploadInfo({
            category: "accountsReceivable",
            filename: this.fileToUpload.name || "",
            companyId: company.companyId,
          });
          const upload = await ApiResource.uploadFile({
            url: resourceInfo.url,
            file: this.fileToUpload,
          });
          if (upload) {
            await invoiceModule.importRecievables({
              companyId: company.companyId,
              year: CommonUtil.getFiscalYear(this.yearMonth),
              month: this.month,
              filePath: resourceInfo.filePath,
            });
            this.importPollingTimerId = setInterval(() => {
              this.pollingUpload();
            }, 10000);
            this.fileUploadModal = false;
          }
        } catch {
          this.isProcessingImport = false;
          alert("アップロード処理でエラーが発生しました。");
          return;
        }
      }
    },
    async pollingUpload() {
      await invoiceModule.checkImportStatus();
      if (this.importStatus === "success") {
        this.listInvoices();
        this.isProcessingImport = false;
        if (this.importPollingTimerId) {
          clearInterval(this.importPollingTimerId);
        }
      }
    },
    // export
    async exportSales() {
      const company = LocalDataService.getCompany();
      if (company) {
        alert('エクスポートを開始しました。\n※完了まで1-2分程度かかる場合があります。');
        this.isProcessingExport = true;
        await invoiceModule.exportSales({
          companyId: company.companyId,
          year: CommonUtil.getFiscalYear(this.yearMonth),
          month: this.month,
        });
        this.exportPollingTimerId = setInterval(() => {
          this.pollingExport();
        }, 10000);
      }
    },
    async pollingExport() {
      await invoiceModule.checkExportStatus();
      if (this.exportStatus) {
        this.isProcessingExport = false;
        invoiceModule.setExportId(null);

        if (this.exportPollingTimerId) {
          clearInterval(this.exportPollingTimerId);
        }
      }
    },
    getInvoiceStatusText(value: string): string {
      return this.invoiceStatusOptions.find((i) => i.value == value)?.label || "";
    },
  },
  async mounted() {
    if (this.$route.params.yearMonth) {
      this.yearMonth = Array.isArray(this.$route.params.yearMonth)
        ? this.$route.params.yearMonth[0]
        : this.$route.params.yearMonth;
    }

    await this.listInvoices();
  },
});
</script>

<style lang="scss" scoped>
.v-btn--disabled {
  background-color: #e5e5e5 !important;
  color: #888888 !important;
  opacity: 1 !important;
}
.custom-dense-table :deep(th) {
  font-size: 0.8rem !important;
  font-weight: bold !important;
  white-space: nowrap !important;
  word-break: keep-all !important;
}
.custom-dense-table :deep() {
  --v-table-header-height: 40px; /* ヘッダーの高さを少し低く */
  --v-table-row-height: 40px;    /* 行の高さを少し低く */
  font-size: 0.8rem;             /* 全体のフォントサイズ（デフォルトは 0.875rem = 14px） */
}
:deep(.v-data-table table) {
  table-layout: fixed !important;
}
.custom-dense-table :deep(th:first-child),
.custom-dense-table :deep(td:first-child) {
  width: 50px !important;      /* お好みの幅（45px〜55pxがベスト） */
  max-width: 15px !important;
  min-width: 15px !important;
  padding: 0 !important;       /* 余白を削ってさらに詰める */
  text-align: center !important;
}

/* チェックボックス自体の余白もリセットして中央に寄せる */
.custom-dense-table :deep(.v-selection-control) {
  justify-content: center !important;
}
</style>